/* eslint-disable @typescript-eslint/no-explicit-any */
import { Avatar, Stack, Typography } from '@mui/material';
import {
  ScheduleComponent,
  Inject,
  ViewDirective,
  ViewsDirective,
  RenderCellEventArgs,
  ResourceDirective,
  ResourcesDirective,
  Resize,
  DragAndDrop,
  Day,
  TimelineViews,
  View,
} from '@syncfusion/ej2-react-schedule';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import { RootState } from 'packages/blu-booking/src/store';
import { getConfig } from '../../../utility_objects/WhiteCache';
import {
  AppointmentMouseDown,
  AppointmentMouseUp,
} from '../../../utility_objects/AppointmentMouseUp';
import { CalendarEventTemplate } from '../../../UI/CalendarEventTemplate/CalendarEventTemplate';
import { CalendarEventQuickInfo } from '../../../UI/CalendarEventQuickInfo/CalendarEventQuickInfo';
import { AppointmentRender } from '../../../utility_objects/AppointmentRender';
import { AppointmentResize } from '../../../utility_objects/AppointmentResize';
import { AppointmentDragDrop } from '../../../utility_objects/AppointmentDragDrop';
import { appointmentFormPopupOpen } from '../../../utility_objects/AppointmentFormPopupOpen';
import ImageDefault from '../../../../../resources/utentebase.png';
import { isTablet } from 'react-device-detect';
import { UserCalendarSetSelectedDate } from '../store';
import { useSchedulerPersistence } from '../hooks/useSchedulerPersistence';
import { useSchedulerRefresh } from '../hooks/useSchedulerRefresh';
import { CalendarLoader } from '../../../UI/CalendarLoader/CalendarLoader';
import {
  CellStatus,
  WorkHoursCellTemplate,
} from '../../../UI/WorkHoursCellTemplate/WorkHoursCellTemplate';

interface SchedulerProps {
  calendarPointOfSale: any;
  shiftPointOfSale: any;
}

export const Scheduler = ({
  calendarPointOfSale,
  shiftPointOfSale,
}: SchedulerProps) => {
  const [calendarPointOfSaleWorkHours, calendarPointOfSaleUsersWork] =
    calendarPointOfSale ?? [];
  const [shiftPointOfSaleWorkHours] = shiftPointOfSale ?? [];
  const { appointments, loadingAppointments } = useSelector(
    (state: RootState) => state.bluBookingaAppointments
  );
  const dispatch = useDispatch();

  const {
    selectedDate,
    selectedStudi,
    selectedServices,
    selectedPointOfSale,
    zoom,
    view,
  } = useSelector((state: RootState) => state.bluBookingUserCalendar);
  const { sessionProfilo, focus10 } = getConfig();
  const scheduleObj = useRef<ScheduleComponent>(null);

  const getCellStatus = useCallback(
    (date: Date, type: string, groupIndex?: number): CellStatus => {
      const calWH = calendarPointOfSaleWorkHours;
      const shiftWH = shiftPointOfSaleWorkHours;
      const shift = shiftPointOfSale;
      const usersWork = calendarPointOfSaleUsersWork;

      if (!calWH && !shiftWH) return { code: 0, color: 'var(--closed-color)' };

      const usersList = usersWork?.map((user: any) => user.userId).flat() ?? [];
      if (groupIndex === undefined)
        return { code: 0, color: 'var(--closed-color)' };

      const userIdCalendar = usersList[groupIndex];
      const time = dayjs(date).format('HH:mm:ss');
      const check = (calendar: any) =>
        calendar &&
        ((time >= calendar.startTime1 && time < calendar.endTime1) ||
          (time >= calendar.startTime2 && time < calendar.endTime2));

      const calWH1 =
        calWH && userIdCalendar in calWH ? calWH[userIdCalendar] : undefined;
      const shiftWH1 =
        shiftWH && userIdCalendar in shiftWH
          ? shiftWH[userIdCalendar]
          : undefined;

      if (type === 'monthCells') {
        if (calWH)
          return {
            code: 2,
            color: 'var(--base-foreground-color)',
          };
        if (shift) return { code: 1, color: 'var(--alt-background-color)' };
        return { code: 0, color: 'var(--closed-color)' };
      }

      if (check(calWH1))
        return {
          code: 2,
          color: 'var(--base-foreground-color)',
        };
      if (check(shiftWH1))
        return { code: 1, color: 'var(--alt-background-color)' };
      return { code: 0, color: 'var(--closed-color)' };
    },
    [
      calendarPointOfSaleWorkHours,
      shiftPointOfSaleWorkHours,
      shiftPointOfSale,
      calendarPointOfSaleUsersWork,
    ]
  );

  const cellTemplate = useCallback(
    (props: any) => (
      <WorkHoursCellTemplate
        date={props.date}
        type={props.type}
        groupIndex={props.groupIndex}
        getStatus={getCellStatus}
      />
    ),
    [getCellStatus]
  );

  // ─── Syncfusion lifecycle ──────────────────────────────────────
  useSchedulerPersistence(scheduleObj, selectedDate, focus10);

  const onActionComplete = (args: any) => {
    if (args.requestType === 'dateNavigate') {
      const schedulerInstance = scheduleObj.current;
      const newDate = schedulerInstance?.selectedDate;
      if (newDate) dispatch(UserCalendarSetSelectedDate(newDate));
    }
  };

  function dateHeaderTemplate(props: any) {
    const date: string = dayjs(props.date).format('ddd D MMMM YYYY');
    return (
      <div style={{ position: 'fixed', width: '100%', marginTop: '-10px' }}>
        <div
          style={{
            width: 'fit-content',
            fontWeight: 'bold',
            paddingLeft: '10px',
          }}
        >
          <Typography
            fontSize="14px"
            fontWeight="500"
            sx={{ color: 'unset' }}
          >{`${date.charAt(0).toUpperCase()}${date.slice(1)}`}</Typography>
        </div>
      </div>
    );
  }

  const getWorkDaysStartEndHours = useCallback(
    (args: RenderCellEventArgs) => {
      if (
        args.elementType === 'workCells' ||
        args.elementType === 'monthCells'
      ) {
        args.element.id =
          args.date !== null
            ? dayjs(args.date).format('DD/MM/YYYY HH:mm') ?? ''
            : '';

        args.element.addEventListener('mousedown', AppointmentMouseDown, {
          capture: true,
        });
        args.element.addEventListener(
          'mouseup',
          (event: any) => AppointmentMouseUp(event, 'user'),
          {
            capture: true,
          }
        );

        const usersList = calendarPointOfSaleUsersWork
          .map((user: any) => user.userId)
          .flat();
        if (args?.groupIndex === undefined) return;

        args.element.setAttribute('userid', usersList[args.groupIndex]);
      } else if (args.elementType === 'majorSlot') {
        args.element.setAttribute('style', 'font-size:14px; font-weight: 500;');
      }
    },
    [calendarPointOfSaleUsersWork]
  );

  const getZoom = useCallback(() => {
    return {
      enable: true,
      interval: zoom?.interval,
      slotCount: zoom?.slotCount,
    };
  }, [zoom]);

  const getUsers = useCallback(() => {
    if (calendarPointOfSaleUsersWork === undefined) return [];

    return calendarPointOfSaleUsersWork;
  }, [calendarPointOfSaleUsersWork]);

  const getGroup = useCallback(() => {
    if (calendarPointOfSaleUsersWork !== undefined)
      if (isTablet)
        return {
          group: { byDate: true, resources: ['Utente'] },
          resourceHeaderTemplate: (props: any) => {
            return (
              <div className="template-wrap">
                <div className="resource-detail">
                  <div
                    className="resource-name"
                    style={{ height: 'fit-content', paddingLeft: '10px' }}
                  >
                    <Stack
                      direction="row"
                      flex="1"
                      gap={1}
                      justifyItems={'center'}
                      justifyContent={'center'}
                    >
                      <Avatar
                        sx={{
                          border: '1px solid var(--base-color)',
                          margin: 'auto',
                        }}
                        alt="Foto utente"
                        src={
                          props.resourceData.photo
                            ? props.resourceData.photo
                            : ImageDefault
                        }
                      />
                      <Stack
                        direction="row"
                        flex="1"
                        gap={1}
                        sx={{ m: 'auto' }}
                      >
                        {props.resourceData.username}
                      </Stack>
                    </Stack>
                  </div>
                </div>
              </div>
            );
          },
        };
      else
        return {
          group: { byDate: true, resources: ['Utente'] },
          resourceHeaderTemplate: (props: any) => {
            return (
              <div style={{ height: '97px', margin: 'auto' }}>
                <Avatar
                  sx={{
                    border: '1px solid var(--base-color)',
                    margin: 'auto',
                  }}
                  alt="Foto utente"
                  src={
                    props.resourceData.photo
                      ? props.resourceData.photo
                      : ImageDefault
                  }
                />
                <div
                  style={{
                    width: '100%',
                    paddingTop: '10px',
                    textAlign: 'center',
                  }}
                >
                  {`${props.resourceData.username}`}
                </div>
              </div>
            );
          },
        };
    return {};
  }, [calendarPointOfSaleUsersWork]);

  const getModules = useMemo(() => {
    if (!sessionProfilo.agendaModifica) return [Day, TimelineViews];
    return [Day, DragAndDrop, Resize, TimelineViews];
  }, [sessionProfilo.agendaModifica]);

  // ─── Data refresh ──────────────────────────────────────────
  useSchedulerRefresh(scheduleObj, loadingAppointments);

  return (
    <ScheduleComponent
      id="-user-schedule-persistence"
      ref={scheduleObj}
      cssClass={`user${view === 'Day' ? '-day' : ''}-schedule-cell-dimension`}
      width="100%"
      height="100%"
      timeScale={getZoom()}
      selectedDate={selectedDate}
      currentView={view as View}
      dateFormat="dd/MM/yyyy"
      locale="it"
      minDate={new Date(2000, 0, 1)}
      enablePersistence={true}
      actionComplete={onActionComplete}
      eventSettings={{
        dataSource: appointments,
        // dataSource gestito da useSchedulerRefresh
        template: (props: any) => <CalendarEventTemplate {...props} />,
      }}
      popupOpen={(args) =>
        appointmentFormPopupOpen(
          args,
          [{ id: args.data.userId }],
          selectedStudi,
          selectedServices,
          selectedPointOfSale?.id
        )
      }
      dragStop={(args: any) => AppointmentDragDrop(args, appointments, 'user')}
      resizeStop={(args: any) => AppointmentResize(args, appointments)}
      firstDayOfWeek={1}
      renderCell={getWorkDaysStartEndHours}
      cellTemplate={cellTemplate}
      startHour="07:00"
      eventRendered={(args) => AppointmentRender(args)}
      dateHeaderTemplate={dateHeaderTemplate}
      quickInfoTemplates={
        {
          header: (_: any) => null,
          content: (props: any) => <CalendarEventQuickInfo {...props} />,
        } as any
      }
      {...getGroup()}
    >
      <ResourcesDirective>
        <ResourceDirective
          field="userId"
          title="Utenti"
          name="Utente"
          allowMultiple={true}
          dataSource={getUsers()}
          textField="name"
          idField="userId"
          colorField="color"
        />
      </ResourcesDirective>
      <ViewsDirective>
        <ViewDirective option="Day" />
        <ViewDirective option="TimelineDay" />
      </ViewsDirective>
      <Inject services={getModules} />
    </ScheduleComponent>
  );
};
